/**
 * Searchable US states — each maps to a negative pseudo-ID (like cities)
 * and the supported university IDs in that state.
 */
import {
    CONNECTICUT_UNIVERSITY_IDS,
    FLORIDA_UNIVERSITY_IDS,
    NEW_JERSEY_UNIVERSITY_IDS,
    NEW_YORK_UNIVERSITY_IDS,
} from './supportedUniversities';

export interface SupportedState {
    id: string;
    /** Capsule / suggestion label, e.g. "Florida" */
    label: string;
    abbreviation: string;
    /** Unique negative ID for UI state picks (avoid colliding with city IDs). */
    universityId: number;
    universityIds: number[];
    aliases?: string[];
    mapCenter?: { longitude: number; latitude: number; zoom: number };
}

export const SUPPORTED_STATES: SupportedState[] = [
    {
        id: 'florida',
        label: 'Florida',
        abbreviation: 'FL',
        universityId: -5001,
        universityIds: FLORIDA_UNIVERSITY_IDS,
        aliases: ['fl', 'florida state', 'fla'],
        mapCenter: { longitude: -81.5158, latitude: 27.6648, zoom: 6.2 },
    },
    {
        id: 'new-york',
        label: 'New York',
        abbreviation: 'NY',
        universityId: -5002,
        universityIds: NEW_YORK_UNIVERSITY_IDS,
        aliases: ['ny', 'new york state', 'nys'],
        mapCenter: { longitude: -75.5, latitude: 42.9, zoom: 6.0 },
    },
    {
        id: 'new-jersey',
        label: 'New Jersey',
        abbreviation: 'NJ',
        universityId: -5003,
        universityIds: NEW_JERSEY_UNIVERSITY_IDS,
        aliases: ['nj', 'new jersey state'],
        mapCenter: { longitude: -74.5, latitude: 40.1, zoom: 7.5 },
    },
    {
        id: 'connecticut',
        label: 'Connecticut',
        abbreviation: 'CT',
        universityId: -5004,
        universityIds: CONNECTICUT_UNIVERSITY_IDS,
        aliases: ['ct', 'conn'],
        mapCenter: { longitude: -72.7, latitude: 41.6, zoom: 7.8 },
    },
];

const normalizeStateText = (value: string) =>
    value.toLowerCase().replace(/[^a-z0-9\s]/g, '').replace(/\s+/g, ' ').trim();

/** Given a university_id (negative state pick), return the matching state. */
export const getStateByUniversityId = (universityId: number): SupportedState | undefined =>
    SUPPORTED_STATES.find((s) => s.universityId === universityId);

/** Exact match a typed query to a supported state (label / abbr / alias). */
export function matchSupportedStateFromInput(input: string): SupportedState | undefined {
    const q = normalizeStateText(input);
    if (!q) return undefined;
    const compact = q.replace(/\s+/g, '');

    return SUPPORTED_STATES.find((state) => {
        const labelNorm = normalizeStateText(state.label);
        const abbrNorm = state.abbreviation.toLowerCase();
        const aliases = (state.aliases || []).map(normalizeStateText);
        return (
            labelNorm === q ||
            abbrNorm === q ||
            abbrNorm === compact ||
            aliases.some((a) => a === q)
        );
    });
}

/** Match typed query to supported states (empty query → none). */
export function matchSupportedStates(query: string): SupportedState[] {
    const q = normalizeStateText(query);
    if (!q) return [];

    const compact = q.replace(/\s+/g, '');

    return SUPPORTED_STATES.filter((state) => {
        const labelNorm = normalizeStateText(state.label);
        const abbrNorm = state.abbreviation.toLowerCase();
        const aliases = (state.aliases || []).map(normalizeStateText);
        return (
            labelNorm === q ||
            labelNorm.startsWith(q) ||
            labelNorm.includes(q) ||
            abbrNorm === q ||
            abbrNorm === compact ||
            aliases.some((a) => a === q || a.startsWith(q) || a.includes(q))
        );
    });
}

/** Strip trailing ", XX" state abbreviation from a city label for capsule display. */
export function shortCityLabel(label: string): string {
    return label.replace(/,\s*[A-Z]{2}\s*$/, '').trim();
}
